import { Dialog } from 'primeng/dialog'

import { checkIsElementAttached } from '../../layout/utils'

const shownDialogs = new Set<Dialog>()
let observer: MutationObserver | undefined

export function patchDialog() {
  /**
   * Patch the Dialog to close dialogs that the custom route reuse strategy detached with their page
   *
   * A dialog appended to the body stays there when its page is detached instead of destroyed, and the page's change
   * detection stops: an open dialog stays on screen over the next page, and one that was closing (a sheet whose
   * button navigates) never finishes. While a dialog is shown, its host is checked after every DOM change; once it
   * left the document, the dialog closes, which also tells the page it is closed for when the page is shown again.
   */
  const orig__Dialog_onBeforeEnter = Dialog.prototype.onBeforeEnter
  Dialog.prototype.onBeforeEnter = function (...args) {
    orig__Dialog_onBeforeEnter.call(this, ...args)
    if (this.$appendTo() !== 'self') {
      shownDialogs.add(this)
      observer ??= new MutationObserver(() => shownDialogs.forEach(closeIfDetached))
      observer.observe(document.body, { childList: true, subtree: true })
    }
  }

  const orig__Dialog_onAfterLeave = Dialog.prototype.onAfterLeave
  Dialog.prototype.onAfterLeave = function (...args) {
    orig__Dialog_onAfterLeave.call(this, ...args)
    if (!checkIsElementAttached(this.el.nativeElement)) {
      this.cd.detectChanges()
    }
    forget(this)
  }

  /**
   * Skip PrimeNG's body scroll lock: html and body never scroll (reset.scss), and the lock sets a scrollbar width
   * variable on body, measured with a forced layout, which every element then inherits and restyles on each open.
   */
  const orig__Dialog_enableModality = Dialog.prototype.enableModality
  Dialog.prototype.enableModality = function (...args) {
    // `modal` only decides the lock here; the mask and its click listener follow it elsewhere
    const modal = this.modal
    this.modal = false
    try {
      orig__Dialog_enableModality.call(this, ...args)
    } finally {
      this.modal = modal
    }
  }

  const orig__Dialog_onDestroy = Dialog.prototype.onDestroy
  Dialog.prototype.onDestroy = function (...args) {
    orig__Dialog_onDestroy.call(this, ...args)
    forget(this)
  }
}

function closeIfDetached(dialog: Dialog) {
  if (checkIsElementAttached(dialog.el.nativeElement)) {
    return
  }
  if (dialog.visible) {
    dialog.close(new Event('close'))
  }
  // Runs the leave animation; onAfterLeave renders the end of it
  dialog.cd.detectChanges()
}

function forget(dialog: Dialog) {
  shownDialogs.delete(dialog)
  if (!shownDialogs.size) {
    observer?.disconnect()
  }
}
